import { FullPageCard, NavigationPage } from '@/app/components'
import useViewModel from './useEditCostCategoriesViewModel'
import { LoadingContainer } from '@/components/Animations/FadeContainer/FadeContainer'
import { Button, Text } from '@/components'
import TextFormField from '@/components/forms/text-form-field'
import { useForm, UseFormRegister } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  AlertCircleIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  LoaderCircle,
} from 'lucide-react'
import { getCostCategoryDisplayElements } from '../utils'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import Dialog from '@/app/components/Dialog/Dialog'
import { CostCategoryDisplay } from '@/types/thermostatCostCategoryControls'
import {
  UpdateCostCategoriesFormData,
  updateCostCategoriesSchema,
} from '@/hooks/useCostCategories'

export default function EditCostCategories() {
  const viewModel = useViewModel()

  return (
    <FullPageCard>
      <NavigationPage
        id="cost-categories"
        title="Edit Cost Categories"
        subtitle="Set the thresholds for low and high price tiers"
        className="p-0"
      >
        <LoadingContainer
          isLoading={viewModel.isLoading}
          className="h-full"
          loadedContent={<EditCostCategoriesContent {...viewModel} />}
        />
      </NavigationPage>
    </FullPageCard>
  )
}

function EditCostCategoriesContent({
  lowPriceCategory,
  highPriceCategory,
  localCostCategories,
  updateLocalCostCategories,
  submitUpdateForm,
  onDiscardClick,
}: ReturnType<typeof useViewModel>) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const {
    register,
    formState: { errors, isValid, isSubmitting },
    handleSubmit,
    watch,
  } = useForm<UpdateCostCategoriesFormData>({
    resolver: yupResolver(updateCostCategoriesSchema),
    mode: 'onChange',
    defaultValues: {
      lowPriceCost: (lowPriceCategory?.maxValue ?? 1) / 100,
      highPriceCost: (highPriceCategory?.minValue ?? 1) / 100,
    },
  })
  const onSubmit = (data: UpdateCostCategoriesFormData) => {
    submitUpdateForm(data)
    setIsDialogOpen(false)
  }

  useEffect(() => {
    const subscription = watch((value) => {
      const schemaValid = updateCostCategoriesSchema.isValidSync({
        lowPriceCost: value.lowPriceCost,
        highPriceCost: value.highPriceCost,
      })
      if (!schemaValid) {
        return
      }
      updateLocalCostCategories(
        localCostCategories,
        (value?.lowPriceCost ?? 0) * 100,
        (value?.highPriceCost ?? 0) * 100,
      )
    })
    return () => subscription.unsubscribe()
  }, [watch, updateLocalCostCategories, localCostCategories])

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-4 h-full"
    >
      <section className="flex flex-col">
        <CostControl
          title="Low Cost Hour"
          subtitle="Less than <"
          register={register}
          name="lowPriceCost"
          icon={
            <ArrowDownIcon
              className="text-base-green-700 flex-none"
              size={18}
            />
          }
          error={errors.lowPriceCost?.message}
        />
        <CostControl
          title="High Cost Hour"
          subtitle="More than >"
          register={register}
          name="highPriceCost"
          icon={
            <ArrowUpIcon className="text-base-red-600 flex-none" size={18} />
          }
          error={errors.highPriceCost?.message}
        />
      </section>
      <section className="flex flex-col gap-2">
        <Text variant="subheader" className="text-base-base-900">
          Cost Categories
        </Text>
        <CostCategoriesList costCategories={localCostCategories} />
      </section>
      <span className="flex-auto" />
      <Button
        id="update-cost-categories"
        disabled={!isValid || isSubmitting}
        className="mt-2"
        onClick={() => setIsDialogOpen(true)}
      >
        Save
      </Button>
      <Dialog
        title="Do you want to save your edits?"
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      >
        <div className="flex flex-col gap-2">
          <CostCategoriesList costCategories={localCostCategories} />
          <Button
            id="discard-cost-category-edits"
            variant="secondary"
            onClick={() => {
              setIsDialogOpen(false)
              onDiscardClick()
            }}
          >
            Discard
          </Button>
          <Button
            id="save-cost-category-edits"
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            className="flex gap-1 items-center"
            disabled={isSubmitting}
          >
            {isSubmitting && (
              <LoaderCircle className="animate-spin" size={16} />
            )}
            Confirm
          </Button>
        </div>
      </Dialog>
    </form>
  )
}

function CostCategoriesList({
  costCategories,
}: {
  costCategories: CostCategoryDisplay[]
}) {
  return (
    <div className="flex flex-col gap-2 mb-2">
      {costCategories.map((c) => {
        const { title, icon, description } = getCostCategoryDisplayElements({
          minValue: c.minValue,
          maxValue: c.maxValue,
          type: c.type,
        })
        return (
          <div key={c.id} className="flex flex-row gap-2 justify-between">
            <Text variant="body2" className="flex gap-1 items-center">
              {icon} {title}
            </Text>
            <Text variant="body2">{description}</Text>
          </div>
        )
      })}
    </div>
  )
}

function CostControl({
  title,
  subtitle,
  register,
  name,
  icon,
  error,
}: {
  title: string
  subtitle: string
  register: UseFormRegister<UpdateCostCategoriesFormData>
  name: keyof UpdateCostCategoriesFormData
  icon: React.ReactNode
  error?: string
}) {
  return (
    <div className="flex flex-col border-b border-base-grey-100 border-solid py-2 pt-4">
      <div className="flex justify-between items-center gap-1">
        <div className="flex flex-row gap-1">
          <div className="translate-y-0.5">{icon}</div>
          <div>
            <Text variant="subheader" className="text-base-base-900">
              {title}
            </Text>
            <Text variant="body2" className="font-bold">
              {subtitle}
            </Text>
          </div>
        </div>
        <TextFormField
          name={name}
          label="Cost per kWh"
          register={register}
          className="w-28"
          prefix="$"
          error={error}
        />
      </div>
      <Text
        variant="body2"
        className={clsx(
          'text-base-red-600 flex items-center gap-1 self-end',
          error ? 'opacity-100' : 'opacity-0',
        )}
      >
        <AlertCircleIcon size={14} />
        {error ?? '&nbsp'}
      </Text>
    </div>
  )
}
